import React, { useState } from 'react';
import {
  Box,
  Typography,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Icon } from '../../icons';
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from '../../constants/color';
import { useAuth } from '../../context/authContext';

const TechnicalSettings = () => {
  useAuth();
  const [settings, setSettings] = useState({
    autoSave: true,
    emailNotifications: false,
    requireApproval: true,
    defaultPriority: 'medium',
    observationRetention: 90,
  });

  const [checklistTemplates, setChecklistTemplates] = useState([
    {
      id: 1,
      name: 'Foundation Inspection',
      category: 'Structural',
      items: 12,
      isActive: true,
      lastUpdated: '2026-01-15',
    },
    {
      id: 2,
      name: 'Safety Compliance',
      category: 'Safety',
      items: 8,
      isActive: true,
      lastUpdated: '2026-01-20',
    },
    {
      id: 3,
      name: 'Quality Control',
      category: 'Quality',
      items: 15,
      isActive: false,
      lastUpdated: '2026-01-10',
    },
  ]);

  const [reportTemplates, setReportTemplates] = useState([
    {
      id: 1,
      name: 'Daily Technical Report',
      type: 'daily',
      sections: 6,
      isActive: true,
      lastUsed: '2026-02-03',
    },
    {
      id: 2,
      name: 'Weekly Summary',
      type: 'weekly',
      sections: 8,
      isActive: true,
      lastUsed: '2026-02-01',
    },
    {
      id: 3,
      name: 'Monthly Analysis',
      type: 'monthly',
      sections: 12,
      isActive: true,
      lastUsed: '2026-01-31',
    },
  ]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleTemplateToggle = (templateId, type) => {
    if (type === 'checklist') {
      setChecklistTemplates(prev =>
        prev.map(template =>
          template.id === templateId
            ? { ...template, isActive: !template.isActive }
            : template
        )
      );
    } else {
      setReportTemplates(prev =>
        prev.map(template =>
          template.id === templateId
            ? { ...template, isActive: !template.isActive }
            : template
        )
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          fontFamily: FONT_FAMILY,
          ...TYPOGRAPHY.H1,
          color: COLORS.TEXT,
          mb: 3,
        }}
      >
        Technical Settings
      </Typography>

      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              backgroundColor: COLORS.BACKGROUND,
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ p: 3, borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.H2,
                  color: COLORS.TEXT,
                }}
              >
                General Settings
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoSave}
                      onChange={(e) => handleSettingChange('autoSave', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: COLORS.PRIMARY,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                      }}
                    >
                      Auto-save observations
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: COLORS.PRIMARY,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                      }}
                    >
                      Email notifications
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.requireApproval}
                      onChange={(e) => handleSettingChange('requireApproval', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: COLORS.PRIMARY,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                      }}
                    >
                      Require approval for observations
                    </Typography>
                  }
                />

                <FormControl fullWidth>
                  <InputLabel
                    sx={{
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Default Priority
                  </InputLabel>
                  <Select
                    value={settings.defaultPriority}
                    onChange={(e) => handleSettingChange('defaultPriority', e.target.value)}
                    sx={{
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <MenuItem value="low" sx={{ fontFamily: FONT_FAMILY }}>Low</MenuItem>
                    <MenuItem value="medium" sx={{ fontFamily: FONT_FAMILY }}>Medium</MenuItem>
                    <MenuItem value="high" sx={{ fontFamily: FONT_FAMILY }}>High</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Observation Retention (days)"
                  type="number"
                  value={settings.observationRetention}
                  onChange={(e) => handleSettingChange('observationRetention', parseInt(e.target.value))}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontFamily: FONT_FAMILY,
                    },
                    '& .MuiInputBase-root': {
                      fontFamily: FONT_FAMILY,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Paper>
        </Grid>

        {/* Checklist Templates */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              backgroundColor: COLORS.BACKGROUND,
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ p: 3, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.H2,
                  color: COLORS.TEXT,
                }}
              >
                Checklist Templates
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="plus" size={16} />}
                sx={{
                  backgroundColor: COLORS.PRIMARY,
                  '&:hover': {
                    backgroundColor: COLORS.SECONDARY,
                  },
                  fontFamily: FONT_FAMILY,
                  textTransform: 'none',
                }}
              >
                Add Template
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Items
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checklistTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell
                        sx={{
                          fontFamily: FONT_FAMILY,
                          color: COLORS.TEXT,
                        }}
                      >
                        {template.name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.category}
                          size="small"
                          sx={{
                            fontFamily: FONT_FAMILY,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: FONT_FAMILY,
                          color: COLORS.TEXT,
                        }}
                      >
                        {template.items}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={template.isActive}
                          onChange={() => handleTemplateToggle(template.id, 'checklist')}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: COLORS.PRIMARY,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: COLORS.PRIMARY,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <Icon name="edit" size={16} color={COLORS.PRIMARY} />
                        </IconButton>
                        <IconButton size="small">
                          <Icon name="trash" size={16} color={COLORS.ERROR} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Report Templates */}
        <Grid item xs={12}>
          <Paper
            sx={{
              backgroundColor: COLORS.BACKGROUND,
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ p: 3, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.H2,
                  color: COLORS.TEXT,
                }}
              >
                Report Templates
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="plus" size={16} />}
                sx={{
                  backgroundColor: COLORS.PRIMARY,
                  '&:hover': {
                    backgroundColor: COLORS.SECONDARY,
                  },
                  fontFamily: FONT_FAMILY,
                  textTransform: 'none',
                }}
              >
                Add Template
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Sections
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Last Used
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 600,
                        color: COLORS.TEXT,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell
                        sx={{
                          fontFamily: FONT_FAMILY,
                          color: COLORS.TEXT,
                          fontWeight: 500,
                        }}
                      >
                        {template.name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.type}
                          color="primary"
                          size="small"
                          sx={{
                            fontFamily: FONT_FAMILY,
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: FONT_FAMILY,
                          color: COLORS.TEXT,
                        }}
                      >
                        {template.sections}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: FONT_FAMILY,
                          color: COLORS.TEXT,
                        }}
                      >
                        {template.lastUsed}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={template.isActive}
                          onChange={() => handleTemplateToggle(template.id, 'report')}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: COLORS.PRIMARY,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: COLORS.PRIMARY,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <Icon name="edit" size={16} color={COLORS.PRIMARY} />
                        </IconButton>
                        <IconButton size="small">
                          <Icon name="download" size={16} color={COLORS.PRIMARY} />
                        </IconButton>
                        <IconButton size="small">
                          <Icon name="trash" size={16} color={COLORS.ERROR} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TechnicalSettings;
